/*
 * WISDOM Institutional & Program Accreditation Maturity Model — React wrapper.
 *
 * The original single-file HTML app is preserved byte-for-byte: its CSS, libraries
 * (SheetJS, JSZip, jsPDF, html2canvas, mp4-muxer), embedded data (SEED / MODEL /
 * Word templates) and app scripts now live in /public/wamm-assets/ and are loaded here
 * in the exact order the browser ran them in the original file. No logic, behaviour
 * or design has been changed.
 *
 * Setup: copy the `public/wamm-assets` folder into your project's `public/` folder,
 * then render <WisdomWAMM /> anywhere. If your app is served from a
 * sub-path, pass assetsBase (e.g. assetsBase="/my-app/wamm-assets/").
 */
import { useEffect, useRef } from 'react';
import { useAuth } from 'context/AuthContext';

const TITLE = 'WISDOM Institutional & Program Accreditation Maturity Model';
const DESCRIPTION =
  'وحدة ويسدوم لجاهزية الاعتماد المؤسسي والبرامجي — WISDOM Institutional & Program Accreditation Maturity Model';

// Same order as the <script> tags in the original HTML file.
const ASSETS = [
  { kind: 'js', file: '01-logos.js' },
  { kind: 'js', file: '02-xlsx.js', id: 'lib-xlsx' },
  { kind: 'js', file: '03-jszip.js' },
  { kind: 'js', file: '04-jspdf.js' },
  { kind: 'js', file: '05-html2canvas.js' },
  { kind: 'js', file: '06-mp4-muxer.js' },
  { kind: 'json', file: 'seed.json', id: 'SEED' },
  { kind: 'json', file: 'model.json', id: 'MODEL' },
  { kind: 'js', file: '07-templates.js' },
  { kind: 'js', file: '08-app-core.js' },
  { kind: 'js', file: '09-app-export.js' },
  { kind: 'js', file: '10-app-wisdom-import.js' },
  { kind: 'js', file: '11-app-video.js' },
  { kind: 'js', file: '12-app-etqk.js' },
];

// The app's original body markup. The app scripts look these elements up by id
// and manage their contents themselves, so React must not re-render them.
const MARKUP =
  '<div id="app"></div>' +
  '<div id="modal" class="modal" hidden></div>' +
  '<div id="busy" class="busy" hidden role="status" aria-live="polite"></div>' +
  '<div id="toast" role="status" aria-live="polite"></div>' +
  '<div id="pdfStage" aria-hidden="true"></div>';

// Module-level state: the app is a page-wide singleton (global scripts, document
// listeners, IndexedDB), so it is loaded once and its DOM is kept alive across
// unmount/remount (route changes, React StrictMode double effects).
let hostNode = null;
let loadPromise = null;

function loadCss(base) {
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = base + 'wisdom.css';
    link.setAttribute('data-wisdom', '');
    link.onload = link.onerror = () => resolve();
    document.head.appendChild(link);
  });
}

async function fetchText(base, a) {
  const res = await fetch(base + a.file);
  if (!res.ok) throw new Error('Failed to load ' + a.file + ' (' + res.status + ')');
  return res.text();
}

// Insert one asset as an inline <script>, exactly like the original page.
// JSON assets become <script id="SEED|MODEL" type="application/json"> because the
// app reads them via getElementById(...).textContent. Inline JS scripts execute
// synchronously on insertion, so all of them run back-to-back before the app's
// own setTimeout(boot, 0) fires — the same order and timing as the original HTML.
function insertScript(a, text) {
  const s = document.createElement('script');
  if (a.kind === 'json') s.type = 'application/json';
  if (a.id) s.id = a.id;
  s.setAttribute('data-wisdom', '');
  s.textContent = a.kind === 'js' ? text + '\n//# sourceURL=' + a.file : text;
  document.body.appendChild(s);
}

function loadApp(base) {
  if (!loadPromise) {
    loadPromise = (async () => {
      document.title = TITLE;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = DESCRIPTION;

      // Download the stylesheet and every asset in parallel first...
      const [, texts] = await Promise.all([
        loadCss(base),
        Promise.all(ASSETS.map((a) => fetchText(base, a))),
      ]);
      // ...then run them all in the original order, with no gaps in between.
      ASSETS.forEach((a, i) => insertScript(a, texts[i]));
    })().catch((err) => {
      loadPromise = null;
      console.error('[WISDOM]', err);
    });
  }
  return loadPromise;
}

export default function WisdomWAMM({ assetsBase = '/wamm-assets/' }) {
  const ref = useRef(null);
  const { userInfo } = useAuth();
  const orgId = userInfo?.organization_id;

  // The app reads window.WAMM_USER_ORG to lock the institution dropdown. If the app
  // is already running (remount after a user switch), re-apply the lock now.
  useEffect(() => {
    if (window.WAMM_USER_ORG === orgId) return;
    window.WAMM_USER_ORG = orgId;
    const etq = window.ETQ;
    if (etq && etq.S.proj) etq.setProject(etq.S.proj.id);
  }, [orgId]);

  useEffect(() => {
    const container = ref.current;
    if (!hostNode) {
      hostNode = document.createElement('div');
      hostNode.style.display = 'contents';
      hostNode.innerHTML = MARKUP;
    }
    container.appendChild(hostNode);
    loadApp(assetsBase.endsWith('/') ? assetsBase : assetsBase + '/');

    return () => {
      // Detach (not destroy) so the app's state survives a remount.
      if (hostNode && hostNode.parentNode === container) container.removeChild(hostNode);
    };
  }, [assetsBase]);

  return <div ref={ref} style={{ display: 'contents' }} />;
}